import { Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import {
  API,
  VisitorTrackerDTO,
  VisitorTrackerScreenAssignmentDTO,
  VisitorTrackerScreenAssignmentsDTO,
  VisitorTrackerScreenDTO,
} from '../../../../services/api/api';
import { ActivatedRoute } from '@angular/router';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { SankeyChartComponent } from '@ngx-seatback/charts';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from '../../../../services/util.service';
import { PermissionService } from '../../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ErrorService } from '../../../../services/error.service';

@Component({
  selector: 'sb-page-location-tracker-assignment',
  templateUrl: './location-tracker-assignment.component.html',
  styleUrls: ['./location-tracker-assignment.component.scss'],
  standalone: false,
})
export class LocationTrackerAssignmentComponent implements OnInit {
  @ViewChild('sankey', { static: false }) sankey: SankeyChartComponent;

  locationId: number;
  loading: boolean;
  rawData: VisitorTrackerScreenAssignmentsDTO;
  sankeyData: { from: string; to: string; value: number; additionalData: any }[];
  externalTracker: VisitorTrackerDTO[];

  constructor(
    private api: API,
    private activatedRoute: ActivatedRoute,
    private modal: ModalService,
    private translate: TranslateService,
    public util: UtilService,
    private errorHandler: ErrorService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.locationId = -1;
    this.loading = true;
    this.rawData = undefined;
    this.sankeyData = [];
    this.externalTracker = [];
  }

  ngOnInit(): void {
    this.activatedRoute.parent.params.subscribe(params => {
      if (params.locationID) {
        this.locationId = +params.locationID;
        this.refreshFromServer().then();
      }
    });
  }

  handleClick($event: any) {
    if ($event) {
      if ($event?._type === 'VisitorTrackerDTO') {
        this.modal
          .modal(
            {
              title: this.translate.instant('lang.globals.tracker') + ' ' + $event.id + ': ' + $event.name,
              icon: this.util.getTrackerIcon($event),
              modalOptions: { size: 'lg' },
            },
            {
              templateId: 'trackerAssignment',
            },
            {
              screens: this.rawData.screens,
              tracker: $event,
              assignments: this.findAssignments($event.id, true),
            }
          )
          .then((result: VisitorTrackerScreenDTO[]) => {
            if (result) {
              this.api
                .dm_visitor_VisitorTracker_assignTrackerToScreens($event.id, result)
                .pipe(takeUntilDestroyed(this.destroyRef), take(1))
                .subscribe({
                  next: () => {
                    this.refreshFromServer().then(() => {
                      this.sankey.updateData(this.sankeyData);
                    });
                  },
                  error: error => {
                    this.errorHandler.errorHandler(error);
                  },
                });
            }
          })
          .catch(() => {});
      } else if ($event._type === 'ScreenFullDTO') {
        this.modal
          .modal(
            {
              title: this.translate.instant('lang.globals.screen') + ' ' + $event.id + ': ' + $event.name,
              icon: 'screen',
              modalOptions: { size: 'lg' },
            },
            {
              templateId: 'screenAssignment',
            },
            {
              screens: $event,
              tracker: this.rawData.trackers,
              assignments: this.findAssignments($event.id, false),
            }
          )
          .then((result: VisitorTrackerScreenDTO[]) => {
            if (result) {
              this.api
                .ss_nm_Screen_assignScreenToTrackers($event.id, result)
                .pipe(takeUntilDestroyed(this.destroyRef), take(1))
                .subscribe({
                  next: () => {
                    this.refreshFromServer().then(() => {
                      this.sankey.updateData(this.sankeyData);
                    });
                  },
                  error: error => {
                    this.errorHandler.errorHandler(error);
                  },
                });
            }
          })
          .catch(() => {});
      }
    }
  }

  findAssignments(id: number, isTracker: boolean): VisitorTrackerScreenAssignmentDTO[] {
    const tempAssignments: any[] = [];
    if (this.rawData) {
      for (const ass of this.rawData.assignments) {
        if (isTracker) {
          if (ass.tracker === Number(id)) {
            const tempAss = {
              screen: this.resolveScreen(ass.screen).id,
              tracker: ass.tracker,
              amount: ass.amount,
            };
            tempAssignments.push(tempAss);
          }
        } else if (ass.screen === Number(id)) {
          const tempAss = {
            screen: ass.screen,
            tracker: this.findSelectedTracker(ass.tracker)?.id,
            amount: ass.amount,
          };
          tempAssignments.push(tempAss);
        }
      }
    }
    return tempAssignments;
  }

  resolveScreen(screenId: number) {
    if (this.rawData?.screens) {
      for (const screen of this.rawData.screens) {
        if (screen.id === screenId) {
          return screen;
        }
      }
    }
  }

  findSelectedTracker(id: number): VisitorTrackerDTO | null {
    if (this.rawData?.trackers) {
      for (const d of this.rawData.trackers) {
        if (d.id === Number(id)) {
          return d;
        }
      }
      return null;
    }
  }

  refreshFromServer() {
    return new Promise(resolve => {
      this.loading = true;
      this.api
        .dm_visitor_VisitorTrackerScreen_retrieveAllAssignmentsAtLocation(this.locationId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (data: VisitorTrackerScreenAssignmentsDTO) => {
            this.externalTracker = [];
            data.trackers.forEach(x => {
              if (x?.location?.id !== this.locationId) {
                this.externalTracker.push(x);
              }
            });
            this.rawData = data;
            this.convertToChartModel(data);
            resolve(true);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    });
  }

  convertToChartModel(data: VisitorTrackerScreenAssignmentsDTO) {
    this.sankeyData = [];
    this.loading = true;
    for (const tracker of data.trackers) {
      this.sankeyData.push({
        from: this.attachPrefix(tracker.id),
        to: undefined,
        value: undefined,
        additionalData: { from: tracker, to: { _type: 'ScreenFullDTO' } },
      });
    }
    const missingData = [];
    for (const ass of data.assignments) {
      for (const singleData of this.sankeyData) {
        if (singleData.from.toString() === this.attachPrefix(ass.tracker)) {
          if (singleData.to === undefined) {
            singleData.to = this.attachPrefix(ass.screen);
            singleData.value = ass.amount;
            singleData.additionalData.to = this.resolveScreen(ass.screen);
          } else {
            missingData.push({
              from: this.attachPrefix(ass.tracker),
              to: this.attachPrefix(ass.screen),
              value: ass.amount,
              additionalData: {
                from: this.findSelectedTracker(ass.tracker),
                to: this.resolveScreen(ass.screen),
              },
            });
          }
        }
      }
    }
    this.sankeyData.push(...missingData);

    this.loading = false;
  }

  attachPrefix(element: number): string {
    return 'ID: ' + element.toString();
  }
}
